import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const PermissionContext = createContext({});

export const usePermissions = () => useContext(PermissionContext);

// Display metadata (labels/icons) for modules — the ONLY frontend source.
// Module/action STRUCTURE comes from the backend catalog
// (GET /api/permissions/modules). Keep keys in sync with backend MODULES;
// a missing entry falls back to a prettified key.
export const MODULES = {
  dashboard: { label: 'Dashboard', icon: '📊' },
  employees: { label: 'Employees', icon: '👥', sensitive: true },
  fleet: { label: 'Fleet', icon: '🚌' },
  dispatch: { label: 'Dispatch', icon: '📋' },
  accidents: { label: 'Accidents', icon: '⚠️', sensitive: true },
  hr_applicants: { label: 'HR / Applicants', icon: '📝', sensitive: true },
  certifications: { label: 'Certifications', icon: '📜' },
  website_cms: { label: 'Website CMS', icon: '🌐' },
  settings: { label: 'Settings', icon: '⚙️' },
  reports: { label: 'Reports', icon: '📈' },
  messages: { label: 'Messages', icon: '💬' },
  notifications: { label: 'Notifications', icon: '🔔' },
  expenses: { label: 'Expenses', icon: '💵' },
  time_off: { label: 'Time Off', icon: '🗓️' }
};

// Canonical ordered action list — drives Settings matrix column order.
// Superset of all actions any module uses.
export const ACTIONS = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  manage: 'Manage',
  export: 'Export',
  submit: 'Submit',
  approve: 'Approve'
};

export const PermissionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [moduleCatalog, setModuleCatalog] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch permissions from API
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions({});
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/permissions/me');
      setPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch the module/action catalog (structure only: { module: [actions] })
  // from the backend — the authoritative MODULES in middleware/permissions.js
  const fetchModuleCatalog = useCallback(async () => {
    if (!isAuthenticated) {
      setModuleCatalog({});
      return;
    }

    try {
      const response = await api.get('/permissions/modules');
      setModuleCatalog(response.data || {});
    } catch (error) {
      console.error('Failed to fetch module catalog:', error);
      setModuleCatalog({});
    }
  }, [isAuthenticated]);

  // Fetch permissions when user changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchPermissions();
      fetchModuleCatalog();
    } else {
      setPermissions({});
      setModuleCatalog({});
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, fetchPermissions, fetchModuleCatalog]);

  /**
   * Check if user has permission for a specific module and action
   */
  const hasPermission = useCallback((module, action = 'view') => {
    // Admin always has full access
    if (user?.role === 'admin') {
      return true;
    }

    // Check permissions object
    if (permissions[module]) {
      return permissions[module].includes(action);
    }

    return false;
  }, [permissions, user?.role]);

  /**
   * Check if user can view a module (shorthand)
   */
  const canView = useCallback((module) => {
    return hasPermission(module, 'view');
  }, [hasPermission]);

  /**
   * Check if user can edit a module (shorthand)
   */
  const canEdit = useCallback((module) => {
    return hasPermission(module, 'edit');
  }, [hasPermission]);

  /**
   * Check if user can create in a module (shorthand)
   */
  const canCreate = useCallback((module) => {
    return hasPermission(module, 'create');
  }, [hasPermission]);

  /**
   * Check if user can delete in a module (shorthand)
   */
  const canDelete = useCallback((module) => {
    return hasPermission(module, 'delete');
  }, [hasPermission]);

  /**
   * Check if user can manage a module (shorthand)
   */
  const canManage = useCallback((module) => {
    return hasPermission(module, 'manage');
  }, [hasPermission]);

  /**
   * Get all accessible modules for menu rendering
   */
  const getAccessibleModules = useCallback(() => {
    if (user?.role === 'admin') {
      return Object.keys(MODULES);
    }

    return Object.keys(MODULES).filter(module => 
      permissions[module] && permissions[module].length > 0
    );
  }, [permissions, user?.role]);

  /**
   * Filter menu items based on permissions
   */
  const filterMenuItems = useCallback((menuItems) => {
    return menuItems.filter(item => {
      if (!item.module) return true; // Items without module requirement always show
      return canView(item.module);
    });
  }, [canView]);

  const value = {
    permissions,
    moduleCatalog,
    loading,
    hasPermission,
    canView,
    canEdit,
    canCreate,
    canDelete,
    canManage,
    getAccessibleModules,
    filterMenuItems,
    refreshPermissions: fetchPermissions,
    MODULES,
    ACTIONS
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Higher-order component to protect routes based on permissions
 */
export const withPermission = (WrappedComponent, module, action = 'view') => {
  return function PermissionWrappedComponent(props) {
    const { hasPermission, loading } = usePermissions();
    
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    if (!hasPermission(module, action)) {
      return (
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this section.</p>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

/**
 * Component to conditionally render children based on permissions
 */
export const PermissionGate = ({ module, action = 'view', children, fallback = null }) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) {
    return null;
  }

  if (!hasPermission(module, action)) {
    return fallback;
  }

  return children;
};

export default PermissionContext;
